import React, { useContext, useEffect, useMemo, useState } from 'react'
import { HeaderContext } from '../../../../views/LayoutWithLeftSidebar/HeaderContext'
import { createPortal } from 'react-dom'
import { Col, Container, Row } from 'react-bootstrap'
import { MeetingPreparationHeader } from './Header/MeetingPreparationHeader'
import { MeetingPreparationInitialization } from './Initialization/MeetingPreparationInitialization'
import { MeetingInfo } from './MeetingInfo/MeetingInfo'
import { Participants } from './Participants/Participants'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectRoomSession, selectRoomSessionIsLoading, startRoomSessionPolling, stopRoomSessionPolling } from '../../../../store/meetingSummary/roomSession'
import { MeetingSummaryToast } from '../MeetingSummary/Toast/MeetingSummaryToast'
import { parseMetadata } from '../../../../common/util'
import { Interactions } from './Interactions/Interactions'
import { selectPreparationStopOnly } from '../../../../store/meetingSummary/display'
import { MeetingPreparationPoller } from './Poller/MeetingPreparationPoller'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { PhoneControls } from './Phone/PhoneControls'
import { useGetMeQuery } from '../../../../store/user/user'
import { IntegrationServiceId } from '../../../../common/integrations'
import { MeetingProviderAccountIntegrationConfig } from '../../../../models/Account'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { InPersonMeeting } from './InPersonMeeting'

export interface MeetingPreparationProps {
  onSessionFinished?: () => void
}

export const MeetingPreparation: React.FC<MeetingPreparationProps> = ({ onSessionFinished }) => {
  const dispatch = useAppDispatch()
  const { portalRef } = useContext(HeaderContext)
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const roomSession = useAppSelector(selectRoomSession)
  const roomSessionIsLoading = useAppSelector(selectRoomSessionIsLoading)
  const { source } = parseMetadata(roomSession?.metadata ?? '')
  const stopOnly = useAppSelector(selectPreparationStopOnly)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [interactionsLoading, setInteractionsLoading] = useState(false)
  const [linkedServiceId, setLinkedServiceId] = useState<string | null>(null)
  const hasPhoneIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.PHONE && i.state === 'live'))
  }, [userData])
  const phoneConfig = useMemo(() => {
    if (!account?.data?.effectiveConfig?.integrations) {
      return null
    }
    return (
      (account.data.effectiveConfig.integrations.find(
        (integration) => integration.type === 'meeting_provider' && integration.serviceId === 'phone',
      ) as MeetingProviderAccountIntegrationConfig) ?? null
    )
  }, [account])

  useEffect(() => {
    dispatch(startRoomSessionPolling())
    return () => {
      dispatch(stopRoomSessionPolling())
    }
  }, [dispatch])

  useEffect(() => {
    if (roomSession && roomSession.finishedAt && roomSession.finishedAt <= new Date().toISOString()) {
      onSessionFinished?.()
    }
  }, [onSessionFinished, roomSession])

  return (
    <div className='full-size-content'>
      <MeetingPreparationInitialization />
      <MeetingPreparationPoller />
      <MeetingSummaryToast />
      {portalRef && portalRef.current ? createPortal(<MeetingPreparationHeader />, portalRef.current) : null}
      <Container className='mw-100 w-100 px-0 d-flex flex-column gap-3'>
        <Row className='mx-0'>
          <Col className='px-0'>
            <MeetingInfo />
          </Col>
        </Row>
        {!roomSessionIsLoading && !!roomSession && (!source || source === 'voice-memo') && roomSession.roomCreatedBy?.id === currentUserId ? (
          <InPersonMeeting accountId={accountId} roomSession={roomSession} />
        ) : null}
        {!roomSessionIsLoading &&
        !!roomSession &&
        !roomSession.isActive &&
        source === 'phone' &&
        roomSession.roomCreatedBy?.id === currentUserId &&
        (phoneConfig?.sharedPhoneLineEnabled || hasPhoneIntegration) ? (
          <Row className='d-flex m-0 justify-content-start align-items-start'>
            <Col className='phone-controls-container'>
              <PhoneControls hasPhoneIntegration={hasPhoneIntegration} phoneConfig={phoneConfig} />
            </Col>
          </Row>
        ) : null}
        {!stopOnly ? (
          <>
            <Row className='mx-0'>
              <Row className='m-0'>
                <Participants interactionsLoading={interactionsLoading} onContactUpdated={(serviceId) => setLinkedServiceId(serviceId)} />
              </Row>
            </Row>
            {roomSession ? (
              <Interactions
                roomSession={roomSession}
                onLoadingChange={setInteractionsLoading}
                contactUpdatedServiceId={linkedServiceId}
                onDismissContactUpdate={() => setLinkedServiceId(null)}
              />
            ) : null}
          </>
        ) : null}
      </Container>
    </div>
  )
}
